import {
  createContext,
  useContext,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from 'react';

export interface Crumb {
  label: string;
  /** Where the crumb links back to. The last crumb is never a link. */
  to?: string;
}

export const CrumbsContext = createContext<Crumb[]>([]);
// Split from the crumbs so a page that sets its crumbs does not re-render
// every time they change.
export const SetCrumbsContext = createContext<Dispatch<
  SetStateAction<Crumb[]>
> | null>(null);

// How many mounted components show a visible page heading of their own.
export const OwnHeadingContext = createContext(0);
export const SetOwnHeadingContext = createContext<Dispatch<
  SetStateAction<number>
> | null>(null);

export const usePageCrumbs = (): Crumb[] => useContext(CrumbsContext);

/** True while the page shows its own visible h1, so no hidden one is needed. */
export const usePageHasOwnHeading = (): boolean =>
  useContext(OwnHeadingContext) > 0;

/**
 * Called by a component that renders the page's visible h1 (the patient's
 * name, say), so the layout's hidden heading steps aside rather than
 * announcing the page twice. A no-op outside the app layout.
 */
export const useOwnPageHeading = (enabled = true): void => {
  const setCount = useContext(SetOwnHeadingContext);
  useEffect(() => {
    if (!setCount || !enabled) return;
    setCount(n => n + 1);
    return () => setCount(n => n - 1);
  }, [setCount, enabled]);
};

/**
 * Adds the page's own crumbs after the route's base crumb in the header, e.g.
 * the patient's name after "Patient Manager". Cleared when the page unmounts.
 * A no-op outside the app layout.
 */
export const useBreadcrumbs = (crumbs: Crumb[]): void => {
  const setCrumbs = useContext(SetCrumbsContext);
  // Keyed on the content so callers can pass a fresh array every render.
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    if (!setCrumbs) return;
    const own = JSON.parse(key) as Crumb[];
    setCrumbs(own);
    // Clear only our own crumbs, never ones another page has since set.
    return () => setCrumbs(current => (current === own ? [] : current));
  }, [setCrumbs, key]);
};
